// Number formatting utilities

export const formatNumber = (value, decimals = 2, compact = false) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  const num = parseFloat(value);

  if (compact) {
    return formatCompactNumber(num, decimals);
  }

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(num);
};

export const formatCompactNumber = (value, decimals = 2) => {
  const num = parseFloat(value);
  
  if (num >= 1e12) {
    return `${(num / 1e12).toFixed(decimals)}T`;
  } else if (num >= 1e9) {
    return `${(num / 1e9).toFixed(decimals)}B`;
  } else if (num >= 1e6) {
    return `${(num / 1e6).toFixed(decimals)}M`;
  } else if (num >= 1e3) {
    return `${(num / 1e3).toFixed(decimals)}K`;
  }
  
  return formatNumber(num, decimals);
};

export const formatCurrency = (value, currency = 'USD', decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '$0.00';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(parseFloat(value));
};

export const formatTokenAmount = (value, decimals = 18, displayDecimals = 6) => {
  if (!value || value === '0') return '0';
  
  try {
    // If it's a BigNumber-like object with toString
    const strValue = value.toString();
    const num = parseFloat(strValue);
    
    if (num === 0) return '0';
    
    // For very small numbers, show more decimals
    if (num < 0.001) {
      return formatNumber(num, 8);
    }
    
    return formatNumber(num, displayDecimals);
  } catch (error) {
    console.error('Error formatting token amount:', error);
    return '0';
  }
};

export const formatPercentage = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }

  return `${formatNumber(parseFloat(value), decimals)}%`;
};

export const formatAddress = (address, startChars = 6, endChars = 4) => {
  if (!address) return '';
  
  if (address.length <= startChars + endChars) {
    return address;
  }
  
  return `${address.slice(0, startChars)}...${address.slice(-endChars)}`;
};

export const formatTxHash = (hash, chars = 8) => {
  if (!hash) return '';
  return `${hash.slice(0, chars)}...${hash.slice(-chars)}`;
};

export const formatTime = (timestamp, format = 'relative') => {
  if (!timestamp) return '';
  
  const date = new Date(timestamp);
  
  if (format === 'relative') {
    return formatTimeAgo(date);
  }
  
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export const formatTimeAgo = (date) => {
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) {
    return 'just now';
  }
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return `${diffInDays}d ago`;
  }
  
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths}mo ago`;
  }
  
  const diffInYears = Math.floor(diffInMonths / 12);
  return `${diffInYears}y ago`;
};

export const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return 'Instant';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes > 0 ? minutes + 'm' : ''}`;
  } else if (minutes > 0) {
    return `${minutes}m ${remainingSeconds > 0 ? remainingSeconds + 's' : ''}`;
  } else {
    return `${remainingSeconds}s`;
  }
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export const formatGasPrice = (gwei) => {
  if (!gwei || gwei === '0') return '0 gwei';
  
  const num = parseFloat(gwei);
  
  if (num >= 1000) {
    return `${formatNumber(num / 1000, 1)}k gwei`;
  }
  
  return `${formatNumber(num, 1)} gwei`;
};

export const formatConfirmations = (confirmations, required = 12) => {
  if (!confirmations) return '0';
  
  const current = Math.min(confirmations, required);
  return `${current}/${required}`;
};

// Validation helpers
export const isValidNumber = (value) => {
  return !isNaN(value) && isFinite(value) && value >= 0;
};

export const isValidAmount = (amount, min = 0, max = Infinity) => {
  const num = parseFloat(amount);
  return isValidNumber(num) && num >= min && num <= max;
};

export const parseNumber = (value, fallback = 0) => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? fallback : parsed;
};

// Price and market data formatting
export const formatPrice = (price, currency = 'USD') => {
  if (!price || price === 0) return '$0.00';
  
  const num = parseFloat(price);
  
  if (num < 0.01) {
    return formatCurrency(num, currency, 6);
  } else if (num < 1) {
    return formatCurrency(num, currency, 4);
  } else {
    return formatCurrency(num, currency, 2);
  }
};

export const formatPriceChange = (change, showSign = true) => {
  if (!change || change === 0) return '0.00%';
  
  const num = parseFloat(change);
  const sign = showSign ? (num >= 0 ? '+' : '') : '';
  
  return `${sign}${formatNumber(num, 2)}%`;
};

export const formatMarketCap = (marketCap) => {
  return formatCurrency(marketCap, 'USD', 0);
};

export const formatVolume = (volume) => {
  return formatCompactNumber(volume, 2);
};

// Export default object with all functions
export default {
  formatNumber,
  formatCompactNumber,
  formatCurrency,
  formatTokenAmount,
  formatPercentage,
  formatAddress,
  formatTxHash,
  formatTime,
  formatTimeAgo,
  formatDuration,
  formatFileSize,
  formatGasPrice,
  formatConfirmations,
  isValidNumber,
  isValidAmount,
  parseNumber,
  formatPrice,
  formatPriceChange,
  formatMarketCap,
  formatVolume,
}; 